import * as React from 'react'

import { Button, Dropdown } from 'semantic-ui-react'

import { Funnel } from '../../types'

type Card = any

type Props = {
  funnel: Funnel
  cards: Card[]
  children(args: {
    filteredCards: Card[]
    filtersElement?: React.ReactNode
  }): React.ReactNode
}

export default function FunnelFilters(props: Props) {
  const { funnel, cards, children } = props

  const [selectedCompanyIds, setSelectedCompanyIds] = React.useState<string[]>(
    [],
  )
  const [selectedPositionIds, setSelectedPositionIds] = React.useState<
    string[]
  >([])

  const isCandidateFunnel = funnel.collection === 'candidates'

  const companyOptions = React.useMemo(() => {
    if (!isCandidateFunnel) return []
    const entries = new Map<
      string,
      { key: string; text: string; value: string }
    >()
    cards.forEach((card: any) => {
      const company = card.company
      if (company?._id && !entries.has(company._id)) {
        entries.set(company._id, {
          key: company._id,
          text: company.name,
          value: company._id,
        })
      }
    })
    return Array.from(entries.values())
  }, [cards, isCandidateFunnel])

  const positionOptions = React.useMemo(() => {
    if (!isCandidateFunnel) return []
    const entries = new Map<
      string,
      { key: string; text: string; value: string }
    >()
    cards.forEach((card: any) => {
      const position = card.position
      if (position?._id && !entries.has(position._id)) {
        const companyName = card.company?.name
        const title = position.title || position.number || '—'
        entries.set(position._id, {
          key: position._id,
          text: companyName ? `${companyName} - ${title}` : title,
          value: position._id,
        })
      }
    })
    return Array.from(entries.values())
  }, [cards, isCandidateFunnel])

  React.useEffect(() => {
    if (!isCandidateFunnel) return
    setSelectedCompanyIds((current) => {
      const allowed = new Set(companyOptions.map((opt) => opt.value as string))
      const next = current.filter((id) => allowed.has(id))
      return next.length === current.length ? current : next
    })
  }, [companyOptions, isCandidateFunnel])

  React.useEffect(() => {
    if (!isCandidateFunnel) return
    setSelectedPositionIds((current) => {
      const allowed = new Set(positionOptions.map((opt) => opt.value as string))
      const next = current.filter((id) => allowed.has(id))
      return next.length === current.length ? current : next
    })
  }, [positionOptions, isCandidateFunnel])

  const filteredCards = React.useMemo(() => {
    if (!isCandidateFunnel) return cards

    return cards.filter((card: any) => {
      const matchesCompany =
        selectedCompanyIds.length === 0 ||
        (card.company && selectedCompanyIds.includes(card.company._id))

      const matchesPosition =
        selectedPositionIds.length === 0 ||
        (card.position && selectedPositionIds.includes(card.position._id))

      return matchesCompany && matchesPosition
    })
  }, [cards, isCandidateFunnel, selectedCompanyIds, selectedPositionIds])

  const handleCompanyChange = React.useCallback((ids: string[]) => {
    setSelectedCompanyIds(ids)
  }, [])

  const handlePositionChange = React.useCallback((ids: string[]) => {
    setSelectedPositionIds(ids)
  }, [])

  const handleClearFilters = React.useCallback(() => {
    setSelectedCompanyIds([])
    setSelectedPositionIds([])
  }, [])

  const filtersElement =
    isCandidateFunnel &&
    (companyOptions.length > 0 || positionOptions.length > 0) ? (
      <div className="flex items-center gap-2">
        {companyOptions.length > 0 && (
          <Dropdown
            placeholder="All Customer"
            selection
            multiple
            closeOnChange={false}
            value={selectedCompanyIds}
            options={companyOptions}
            onChange={(_, dropdownData) =>
              handleCompanyChange((dropdownData.value as string[]) || [])
            }
          />
        )}

        {positionOptions.length > 0 && (
          <Dropdown
            placeholder="All Position"
            selection
            multiple
            closeOnChange={false}
            value={selectedPositionIds}
            options={positionOptions}
            onChange={(_, dropdownData) =>
              handlePositionChange((dropdownData.value as string[]) || [])
            }
          />
        )}

        {(selectedCompanyIds.length > 0 || selectedPositionIds.length > 0) && (
          <Button
            basic
            icon="delete"
            className="!mr-0"
            onClick={handleClearFilters}
          />
        )}
      </div>
    ) : undefined

  return <>{children({ filteredCards, filtersElement })}</>
}
